import { z } from 'zod';
import { financeDateOnlySchema, financeInvoiceCommandIdSchema, financeInvoiceDetailSchema, financeInvoiceIdSchema, financeInvoiceStatusSchema, financeMoneyCentsSchema, } from './finance-invoice.contract.js';
export const financeInvoiceLifecycleCapability = 'finance.invoice-lifecycle.v1';
export const financePaymentsCapability = 'finance.payments.v1';
export const financeInvoiceWriteOffReasonSchema = z.enum([
    'wire-fee',
    'customer-negotiation',
    'settlement',
    'rounding',
    'bad-debt',
    'other',
]);
export const financePaymentMethodSchema = z.enum([
    'credit-card',
    'ach',
    'check',
    'cash',
    'avidxchange',
    'other',
]);
export const financePaymentStatusSchema = z.enum(['pending', 'completed', 'reversed']);
export const financePaymentReversalKindSchema = z.enum([
    'failed', 'refunded', 'voided', 'duplicate', 'other',
]);
const expectedRevisionSchema = z.number().int().positive();
const optionalNoteSchema = z.string().trim().min(1).max(2_000).nullable();
export const financeReviseInvoiceCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedRevision: expectedRevisionSchema,
    action: z.literal('revise'),
    dueDate: financeDateOnlySchema,
    memo: z.string().trim().max(2_000).nullable(),
});
export const financeIssueInvoiceCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedRevision: expectedRevisionSchema,
    action: z.literal('issue'),
});
export const financeVoidInvoiceCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedRevision: expectedRevisionSchema,
    action: z.literal('void'),
    reason: z.string().trim().min(1).max(1_000),
});
export const financeWriteOffInvoiceCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedRevision: expectedRevisionSchema,
    action: z.literal('write-off'),
    amountCents: financeMoneyCentsSchema.min(1),
    reason: financeInvoiceWriteOffReasonSchema,
    note: optionalNoteSchema,
    writtenOffOn: financeDateOnlySchema,
});
export const financeInvoiceLifecycleCommandSchema = z.discriminatedUnion('action', [
    financeReviseInvoiceCommandSchema,
    financeIssueInvoiceCommandSchema,
    financeVoidInvoiceCommandSchema,
    financeWriteOffInvoiceCommandSchema,
]);
export const financePostInvoicePaymentCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedInvoiceRevision: expectedRevisionSchema,
    amountCents: financeMoneyCentsSchema.min(1),
    method: financePaymentMethodSchema,
    reference: z.string().trim().min(1).max(240).nullable(),
    memo: optionalNoteSchema,
    status: z.enum(['pending', 'completed']),
    paymentDate: financeDateOnlySchema,
});
export const financeReverseInvoicePaymentCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedInvoiceRevision: expectedRevisionSchema,
    reversalKind: financePaymentReversalKindSchema,
    reason: z.string().trim().min(1).max(1_000),
});
export const financeCompleteInvoicePaymentCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedInvoiceRevision: expectedRevisionSchema,
});
export const financePaymentSchema = z.strictObject({
    id: z.string().uuid(),
    invoiceId: financeInvoiceIdSchema,
    projectReferenceId: z.string().uuid(),
    revision: z.number().int().positive(),
    amountCents: financeMoneyCentsSchema.min(1),
    method: financePaymentMethodSchema,
    reference: z.string().trim().min(1).max(240).nullable(),
    memo: optionalNoteSchema,
    status: financePaymentStatusSchema,
    paymentDate: financeDateOnlySchema,
    reversedAt: z.string().datetime({ offset: true }).nullable(),
    reversalKind: financePaymentReversalKindSchema.nullable(),
    reversalReason: z.string().trim().min(1).max(1_000).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((payment, context) => {
    if ((payment.status === 'reversed') !==
        (payment.reversedAt !== null && payment.reversalKind !== null && payment.reversalReason !== null)) {
        context.addIssue({
            code: 'custom', path: ['reversedAt'],
            message: 'Reversed Finance payments require retained time and reason evidence.',
        });
    }
    if (payment.status !== 'reversed' &&
        (payment.reversedAt !== null || payment.reversalKind !== null || payment.reversalReason !== null)) {
        context.addIssue({
            code: 'custom', path: ['reversalReason'],
            message: 'A current Finance payment cannot carry reversal evidence.',
        });
    }
});
export const financePaymentRefundSchema = z.strictObject({
    contractVersion: z.literal('finance.payment-reconciliation.v1'),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    paymentId: z.string().uuid(),
    invoiceId: financeInvoiceIdSchema,
    providerKey: z.string().regex(/^[a-z][a-z0-9-]{0,49}$/),
    externalRefundReference: z.string().trim().min(1).max(240),
    amountCents: financeMoneyCentsSchema.min(1),
    currencyCode: z.literal('USD'),
    refundedOn: financeDateOnlySchema,
    processorStatus: z.string().trim().min(1).max(240).nullable(),
    reason: z.string().trim().min(1).max(2_000),
    recordedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const financeInvoiceWriteOffSchema = z.strictObject({
    id: z.string().uuid(),
    invoiceId: financeInvoiceIdSchema,
    projectReferenceId: z.string().uuid(),
    amountCents: financeMoneyCentsSchema.min(1),
    reason: financeInvoiceWriteOffReasonSchema,
    note: optionalNoteSchema,
    writtenOffOn: financeDateOnlySchema,
    createdAt: z.string().datetime({ offset: true }),
});
export const financeInvoiceEventSchema = z.strictObject({
    id: z.string().uuid(),
    invoiceId: financeInvoiceIdSchema,
    invoiceRevision: z.number().int().min(2),
    eventType: z.enum([
        'revised', 'content-revised', 'issued', 'voided', 'written-off',
        'payment-pending', 'payment-posted', 'payment-completed', 'payment-reversed',
        'payment-refunded',
    ]),
    fromStatus: financeInvoiceStatusSchema,
    toStatus: financeInvoiceStatusSchema,
    occurredAt: z.string().datetime({ offset: true }),
});
export const financeInvoiceLedgerSchema = z.strictObject({
    contractVersion: z.literal(financeInvoiceLifecycleCapability),
    invoice: financeInvoiceDetailSchema,
    payments: z.array(financePaymentSchema).max(1_000),
    refunds: z.array(financePaymentRefundSchema).max(1_000).default([]),
    writeOffs: z.array(financeInvoiceWriteOffSchema).max(1_000),
    events: z.array(financeInvoiceEventSchema).max(2_000),
}).superRefine((ledger, context) => {
    const paymentTotal = ledger.payments
        .filter(({ status }) => status === 'completed')
        .reduce((sum, payment) => sum + payment.amountCents, 0);
    const refundTotal = ledger.refunds.reduce((sum, refund) => sum + refund.amountCents, 0);
    const writeOffTotal = ledger.writeOffs.reduce((sum, writeOff) => sum + writeOff.amountCents, 0);
    if (paymentTotal - refundTotal !== ledger.invoice.amountPaidCents) {
        context.addIssue({
            code: 'custom', path: ['payments'],
            message: 'Completed Finance payments less retained refunds must equal the invoice paid total.',
        });
    }
    if (writeOffTotal !== ledger.invoice.amountWrittenOffCents) {
        context.addIssue({
            code: 'custom', path: ['writeOffs'],
            message: 'Finance write-off facts must equal the invoice written-off total.',
        });
    }
    for (const [index, payment] of ledger.payments.entries()) {
        if (payment.invoiceId !== ledger.invoice.id ||
            payment.projectReferenceId !== ledger.invoice.projectReferenceId) {
            context.addIssue({
                code: 'custom', path: ['payments', index],
                message: 'Finance payment identity does not match the invoice ledger.',
            });
        }
    }
    for (const [index, refund] of ledger.refunds.entries()) {
        const payment = ledger.payments.find(({ id }) => id === refund.paymentId);
        if (refund.invoiceId !== ledger.invoice.id || payment === undefined ||
            payment.invoiceId !== refund.invoiceId) {
            context.addIssue({
                code: 'custom', path: ['refunds', index],
                message: 'Finance refund identity does not match the invoice payment ledger.',
            });
        }
    }
    for (const [index, writeOff] of ledger.writeOffs.entries()) {
        if (writeOff.invoiceId !== ledger.invoice.id ||
            writeOff.projectReferenceId !== ledger.invoice.projectReferenceId) {
            context.addIssue({
                code: 'custom', path: ['writeOffs', index],
                message: 'Finance write-off identity does not match the invoice ledger.',
            });
        }
    }
});
export const financeInvoiceLedgerResponseSchema = z.strictObject({
    data: financeInvoiceLedgerSchema,
    idempotent: z.boolean(),
});
export function isFinanceInvoiceOverdue(invoice, asOfDate) {
    return (invoice.status === 'sent' || invoice.status === 'partial') &&
        invoice.amountDueCents > 0 && invoice.dueDate < financeDateOnlySchema.parse(asOfDate);
}
//# sourceMappingURL=finance-invoice-lifecycle.contract.js.map